import { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, RefreshCw, Upload, ShieldCheck, ShieldAlert } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { dt, showError, saveBlob } from "./util";

const Field = ({ label, ...p }) => (
  <label className="block"><span className="text-xs font-semibold text-slate-600">{label}</span><Input {...p} /></label>
);

function KitDialog({ open, onClose, hasKit, onDone }) {
  const [f, setF] = useState({ password: "", current_passphrase: "", passphrase: "", passphrase_confirm: "" });
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setF({ password: "", current_passphrase: "", passphrase: "", passphrase_confirm: "" }); setAck(false); } }, [open]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const ok = f.password && f.passphrase.length >= 12 && f.passphrase === f.passphrase_confirm && ack && (!hasKit || f.current_passphrase);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/backup-keys/recovery-kit", f, { responseType: "blob" });
      saveBlob(r.data, `EmlakPlus-Kurtarma-${r.headers["x-key-fingerprint"] || "anahtar"}.emlakplus-key`);
      toast.success("Kurtarma anahtarı oluşturuldu ve indirildi. Dosyayı çevrimdışı saklayın.");
      onDone(); onClose();
    } catch (e) { await showError(e); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="kit-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>{hasKit ? "Kurtarma anahtarını yenile" : "Kurtarma anahtarı oluştur"}</DialogTitle>
          <DialogDescription>Parola korumalı .emlakplus-key dosyası yalnızca bir kez indirilir. USB veya kasada, sunucudan ayrı saklayın.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Hesap şifreniz" type="password" data-testid="kit-password" value={f.password} onChange={set("password")} />
          {hasKit && <Field label="Mevcut kurtarma parolası" type="password" data-testid="kit-current-passphrase" value={f.current_passphrase} onChange={set("current_passphrase")} />}
          <Field label="Yeni kurtarma parolası (en az 12 karakter)" type="password" data-testid="kit-passphrase" value={f.passphrase} onChange={set("passphrase")} />
          <Field label="Yeni kurtarma parolası (tekrar)" type="password" data-testid="kit-passphrase-confirm" value={f.passphrase_confirm} onChange={set("passphrase_confirm")} />
          {hasKit && <p className="text-xs text-amber-700">Eski anahtar dosyanızı silmeyin: önceki yedeklerin sunucu kaybında açılması için o dosya gerekir.</p>}
          <label className="flex items-start gap-2 text-xs text-slate-700">
            <Checkbox data-testid="kit-ack" checked={ack} onCheckedChange={(v) => setAck(!!v)} />
            <span>Anahtar dosyası veya parola kaybolursa, sunucu kaybında yedeklerin geri getirilemeyeceğini anlıyorum.</span>
          </label>
          <Button className="w-full" data-testid="kit-submit" disabled={!ok || busy} onClick={submit}>{busy ? "Oluşturuluyor..." : "Oluştur ve indir"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AuthDialog({ open, onClose, title, desc, withKit, onSubmit, testid }) {
  const [f, setF] = useState({ password: "", passphrase: "" });
  const [kit, setKit] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setF({ password: "", passphrase: "" }); setKit(null); } }, [open]);
  const go = async () => { setBusy(true); try { await onSubmit(f, kit); onClose(); } catch (e) { await showError(e); } setBusy(false); };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={testid} className="max-w-md">
        <DialogHeader><DialogTitle>{title}</DialogTitle><DialogDescription>{desc}</DialogDescription></DialogHeader>
        <div className="space-y-3">
          {withKit && <label className="block text-xs font-semibold text-slate-600">Kurtarma anahtarı dosyası (.emlakplus-key)
            <input type="file" accept=".emlakplus-key" data-testid={`${testid}-file`} className="block mt-1 text-xs" onChange={(e) => setKit(e.target.files?.[0] || null)} /></label>}
          <Field label="Hesap şifreniz" type="password" data-testid={`${testid}-password`} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
          <Field label="Kurtarma parolası" type="password" data-testid={`${testid}-passphrase`} value={f.passphrase} onChange={(e) => setF({ ...f, passphrase: e.target.value })} />
          <Button className="w-full" data-testid={`${testid}-submit`} disabled={busy || !f.password || !f.passphrase || (withKit && !kit)} onClick={go}>{busy ? "Doğrulanıyor..." : "Onayla"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function KeyPanel({ onChanged }) {
  const [s, setS] = useState(null);
  const [dlg, setDlg] = useState(null);
  const load = () => api.get("/admin/backup-keys").then((r) => { setS(r.data); onChanged?.(); }).catch(showError);
  useEffect(() => { load(); }, []);
  if (!s) return null;
  const active = s.keks.find((k) => k.active);
  const rotate = async (f) => { const r = await api.post("/admin/backup-keys/rotate", f); toast.success(`Yeni şifreleme anahtarı: #${r.data.version.toString(16).toUpperCase()}. Eski yedekler açılmaya devam eder.`); load(); };
  const importKit = async (f, kit) => {
    const fd = new FormData(); fd.append("kit", kit); fd.append("password", f.password); fd.append("passphrase", f.passphrase);
    const r = await api.post("/admin/backup-keys/import-kit", fd); toast.success(`Kurtarma anahtarı geri yüklendi (${r.data.fingerprint})`); load();
  };
  return (
    <Card className="p-5 space-y-4" data-testid="backup-key-panel">
      <div className="flex flex-wrap items-center gap-2">
        <KeyRound className="w-5 h-5 text-emerald-700" />
        <h2 className="font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Anahtar yönetimi</h2>
        {s.recovery ? <Badge className="bg-emerald-700" data-testid="recovery-status-ok"><ShieldCheck className="w-3 h-3 mr-1" />Kurtarma anahtarı hazır</Badge>
          : <Badge className="bg-red-600" data-testid="recovery-status-missing"><ShieldAlert className="w-3 h-3 mr-1" />Kurtarma anahtarı yok</Badge>}
      </div>
      <div className="grid sm:grid-cols-3 gap-3 text-sm">
        <div className="rounded-xl border p-3"><div className="text-xs text-slate-500">Ana anahtar (sunucu gizli ayarı)</div><div className="font-semibold" data-testid="master-status">{s.master_ok ? "Yapılandırıldı" : "Eksik!"}</div></div>
        <div className="rounded-xl border p-3"><div className="text-xs text-slate-500">Aktif şifreleme anahtarı</div><div className="font-semibold" data-testid="kek-version">{active ? `#${active.version.toString(16).toUpperCase()} · ${dt(active.created_at)}` : "İlk yedekte oluşturulur"}</div><div className="text-[11px] text-slate-500">{s.keks.length} sürüm saklanıyor</div></div>
        <div className="rounded-xl border p-3"><div className="text-xs text-slate-500">Kurtarma anahtarı kimliği</div><div className="font-mono font-semibold text-xs" data-testid="recovery-fingerprint">{s.recovery?.fingerprint || "—"}</div><div className="text-[11px] text-slate-500">{s.recovery ? `${dt(s.recovery.created_at)} · ${s.recovery.created_by}` : ""}</div></div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button data-testid="kit-open-btn" onClick={() => setDlg("kit")}><KeyRound className="w-4 h-4 mr-1.5" />{s.recovery ? "Kurtarma anahtarını yenile" : "Kurtarma anahtarı oluştur"}</Button>
        <Button variant="outline" data-testid="rotate-open-btn" disabled={!s.recovery} onClick={() => setDlg("rotate")}><RefreshCw className="w-4 h-4 mr-1.5" />Şifreleme anahtarını yenile</Button>
        <Button variant="outline" data-testid="import-open-btn" onClick={() => setDlg("import")}><Upload className="w-4 h-4 mr-1.5" />Anahtar kurtarma (dosyadan)</Button>
      </div>
      <KitDialog open={dlg === "kit"} hasKit={!!s.recovery} onClose={() => setDlg(null)} onDone={load} />
      <AuthDialog open={dlg === "rotate"} testid="rotate-dialog" onClose={() => setDlg(null)} title="Şifreleme anahtarını yenile" desc="Yeni yedekler yeni anahtarla şifrelenir; eski anahtarlar eski yedekler için saklanır." onSubmit={rotate} />
      <AuthDialog open={dlg === "import"} testid="import-dialog" withKit onClose={() => setDlg(null)} title="Anahtar kurtarma" desc="Yeni veya sıfırlanmış bir sunucuda, çevrimdışı sakladığınız kurtarma anahtarı dosyasını sisteme geri yükler." onSubmit={importKit} />
    </Card>
  );
}
